import { isSecondaryMarketQuotesRtFormPersistedTabContent } from 'apiConnector/tabs/secondaryMarket.tabs.model';
import type { Action } from 'state/actions';
import { addKey, appendArray, patchArray, patchKey, removeArray, removeKey } from 'state/utils/collection';

import type { SecondaryMarketQuoteRtFormMap } from '.';
import { initialQuoteRtForm, initialQuoteRtFormMap } from '.';

export function realtimeQuoteFormReducer(
  state: SecondaryMarketQuoteRtFormMap = initialQuoteRtFormMap,
  action: Action,
): SecondaryMarketQuoteRtFormMap {
  switch (action.type) {
    case 'WORKSPACE_TAB_CREATED':
      if (action.tabType === 'realtimeQuote') {
        return addKey(state, action.id, initialQuoteRtForm);
      }
      return state;
    case 'WORKSPACE_TABS_FETCHED':
      if (action.workspace === 'secondaryMarketQuotes') {
        const cpTabs = action.tabs.filter(t => t.type === 'realtimeQuote');
        return cpTabs.reduce((currentState, cpTab) => addKey(currentState, cpTab.id, initialQuoteRtForm), state);
      }
      return state;
    case 'SECONDARY_MARKET_RT_CLIENT_SELECTED':
      return patchKey(state, action.tabId, () => ({ client: action.client, dealSplits: [] }));

    case 'SECONDARY_MARKET_DEALSPLIT_ROW_CUSTODIAN_CHANGED':
      return patchKey(state, action.tabId, ({ dealSplits: oldDealSplits }) => ({
        dealSplits: patchArray(oldDealSplits, action.rowIndex, () => ({ custodianId: action.custodianId })),
      }));

    case 'SECONDARY_MARKET_DEALSPLIT_ROW_NOTIONAL_CHANGED':
      return patchKey(state, action.tabId, ({ dealSplits: oldDealSplits }) => ({
        dealSplits: patchArray(oldDealSplits, action.rowIndex, () => ({ notional: action.notional })),
      }));

    case 'SECONDARY_MARKET_DEALSPLIT_ROW_ADDED':
      return patchKey(state, action.tabId, ({ dealSplits: oldDealSplits }) => ({
        dealSplits: appendArray(oldDealSplits, { custodianId: action.custodianId, notional: action.notional }),
      }));

    case 'SECONDARY_MARKET_DEALSPLIT_ROW_REMOVED':
      return patchKey(state, action.tabId, ({ dealSplits: oldDealSplits }) => ({
        dealSplits: removeArray(oldDealSplits, action.rowIndex),
      }));
    case 'SECONDARY_MARKET_RT_FORM_FIELD_CHANGED':
      return patchKey(state, action.tabId, () => ({ [action.field]: action.value }));

    case 'WORKSPACE_TAB_REMOVED':
      if (action.workspace === 'secondaryMarketQuotes') {
        return removeKey(state, action.id);
      }
      return state;

    case 'SECONDARY_MARKET_QUOTE_REQUESTED':
      return patchKey(state, action.tabId, () => ({ quoteId: null, quoteStatus: 'PENDING' as const }));

    case 'SECONDARY_MARKET_QUOTE_RECEIVED':
      return patchKey(state, action.tabId, () => ({ quoteId: action.quoteId, quoteStatus: 'SUCCESS' as const }));

    case 'SECONDARY_MARKET_QUOTE_REJECTED':
      return patchKey(state, action.tabId, () => ({ quoteId: null, quoteStatus: 'REJECTED' as const }));
    case 'SECONDARY_MARKET_QUOTE_FAILED':
      return patchKey(state, action.tabId, () => ({
        quoteId: action.quoteId,
        quoteStatus: 'FAILED' as const,
        errorMessage: action.errors.join('\n'),
      }));

    case 'SECONDARY_MARKET_QUOTE_CANCELLED':
      return patchKey(state, action.tabId, () => ({ quoteId: null, quoteStatus: 'NONE' as const }));

    case 'WORKSPACE_TAB_CONTENT_FETCHED':
      if (isSecondaryMarketQuotesRtFormPersistedTabContent(action.tabContent)) {
        const { workspace: _workspace, tabType: _tabType, ...formData } = action.tabContent;
        return patchKey(state, action.tabId, _ => formData);
      }
      return state;

    default:
      return state;
  }
}
